import { IConsoleSearchParams } from '@epicuro-next/data/product';

export type position =
  | 'top center'
  | 'top right'
  | 'middle right'
  | 'bottom right'
  | 'bottom center'
  | 'bottom left'
  | 'middle left'
  | 'top left'
  | 'middle center';

export type Row = IRow | IRowColumns | IRowProductList;
export type Column =
  | IColumnContent
  | IColumnText
  | IColumnContact
  | IColumnMessage
  | IColumnProducts
  | IColumnLinks;

export enum OfferDiscountedPriceType {
  Rrp = 1,
  Wsp = 2,
  All = 3,
}

export interface Configuration {
  offerDiscountedPriceType: 'rrp' | 'wsp' | 'all';
}

export interface StoryDto {
  id: string;
  title?: string;
  startDateUtc: string;
  endDateUtc: string;
  createdBy: string;
  lastUpdatedBy: string;
  story: IStory;
}

export interface IStory {
  rows: Row[];
  configuration?: Configuration;
}

export interface IHasBackground {
  image?: string;
  video?: string;
  youtube?: string;
  backgroundColor?: string;
  backgroundPosition?: position;
}

export interface IHasContent {
  // tslint:disable-next-line:max-union-size
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
}

export interface IRow extends IHasBackground {
  type: StoryRowType;
  height?: number;
  padding?: string;
}

export interface IRowColumns extends IRow {
  columns: Column[];
}

export interface IRowProductList extends IRow {
  productListId?: string;
  products?: IProduct[];
  maxLines?: number;
}

export interface IColumn<T extends StoryColumnType> extends IHasBackground {
  id?: string;
  type: T;
  width: number;
  padding?: string;
}

export interface IStorySearchLink {
  page: 'search';
  parameters: IConsoleSearchParams;
}

export interface IStoryLink {
  page: 'story';
  parameters: {
    id: string;
  };
}

export interface IBasketLink {
  page: 'basket';
  parameters: {
    id: string;
  };
}

export interface IColumnContact extends IColumn<StoryColumnType.ADDRESS> {
  title: string;
  address: string;
  contact: string;
  emailAddress: string;
  phoneNumber: string;
  webSiteUrl: string;
}

export interface IColumnMessage
  extends IColumn<StoryColumnType.TEXT>,
    IHasContent {
  text: string;
}

export type Internallink = IStorySearchLink | IStoryLink | IBasketLink;

export interface IStoryColumnLink {
  title?: string;
  external?: string;
  internal?: Internallink;
}

export interface IHasLink {
  link?: IStoryColumnLink;
}

export interface IHasImage {
  image?: string;
}

export interface IColumnContent
  extends IColumn<StoryColumnType.CONTENT>,
    IHasContent,
    IHasLink,
    IHasImage {
  title?: string;
  subtitle?: string;
  button?: string;
  position?: position;
  offerContentType?: string;
}

export interface IColumnText
  extends IColumn<StoryColumnType.TEXT>,
    IHasContent {
  text: string;
}

export interface IColumnLinks
  extends IColumn<StoryColumnType.LINK_LIST>,
    IHasContent {
  sticky?: boolean;
  links: ReadonlyArray<IStoryColumnLink>;
}

export enum ProductListSource {
  LIST = 'list',
  SEARCH = 'search',
  VARIANT = 'variant',
  REPORTING = 'reporting',
}

interface IColumnProductsBase<T extends ProductListSource>
  extends IColumn<StoryColumnType.PRODUCTS>,
    IHasBackground,
    IHasContent {
  source: T;
}

export interface IColumnProductsList
  extends IColumnProductsBase<ProductListSource.LIST> {
  products?: ReadonlyArray<IProduct>;
}

export interface IColumnProductsSearch
  extends IColumnProductsBase<ProductListSource.SEARCH> {
  params: IConsoleSearchParams;
}

export interface IColumnProductsVariants
  extends IColumnProductsBase<ProductListSource.VARIANT> {
  products: ReadonlyArray<IProduct>;
}

export interface IColumnProductsReporting
  extends IColumnProductsBase<ProductListSource.REPORTING> {
  reportingQueryName: string;
}

export type IColumnProducts =
  | IColumnProductsList
  | IColumnProductsSearch
  | IColumnProductsVariants
  | IColumnProductsReporting;

export interface IProductReference {
  productId: string;
  brandId: string;
}

export interface IProduct {
  productReference: IProductReference;
  title?: string;
}

export enum StoryColumnType {
  CONTENT = 'content',
  PRODUCTS = 'products',
  TEXT = 'text',
  ADDRESS = 'address',
  LINK_LIST = 'linkList',
}

export enum StoryRowType {
  COLUMNS = 'columns',
  PRODUCT_LIST = 'productList',
}
